import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { pptToSg, readSalinityUnit, salinityUnitLabel, sgDecimals } from '../salinity.js';
import { cToF, fToC, getDisplayUnits, tempLabel } from '../units.js';

// ICP results — a full page (a destination you browse, not a sheet). The
// "blood panel" read of one test, the list of every test with the keeper's
// own PDF, what changed since the previous test, and a trend chart per
// element. Everything is judged on the server (server/icp-analysis.js); this
// file only draws it.

const STATUS_COLOR = {
  ok: 'var(--ok, #4ade80)',
  clean: 'var(--ok, #4ade80)',
  low: 'var(--amber, #f0b429)',
  high: 'var(--amber, #f0b429)',
  detected: 'var(--alert)',
  unknown: 'var(--text-dim)',
  tracked: 'var(--text-dim)',
};
// 'tracked': a quarantine or freshwater tank's element, shown but never
// judged against a reef range (the same LOGGED word Home uses).
const STATUS_LABEL = {
  ok: 'OK',
  clean: 'CLEAN',
  low: 'LOW',
  high: 'HIGH',
  detected: 'DETECTED',
  tracked: 'LOGGED',
};
const GROUP_LABEL = {
  contaminant: 'Contaminants & metals',
  major: 'Majors',
  nutrient: 'Nutrients',
  trace: 'Trace elements',
  lab: "Also measured (the lab's range)",
};
const GROUP_ORDER = ['contaminant', 'major', 'nutrient', 'trace', 'lab'];

export function fmtNum(v) {
  if (!Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  const dp = a >= 100 ? 0 : a >= 10 ? 1 : a >= 1 ? 2 : a >= 0.01 ? 3 : 4;
  return String(Number(v.toFixed(dp)));
}

// Reefers read trace elements and metals in µg/L, not 0.00032 ppm. Only the
// display moves; the server's numbers and bands stay in ppm.
export function displayScale(item) {
  if (item.unit !== 'ppm' || (item.kind !== 'trace' && item.kind !== 'contaminant')) {
    return { factor: 1, unit: item.unit || '' };
  }
  const top = Math.max(
    Math.abs(item.value || 0),
    item.band ? item.band[1] : 0,
    item.lab_band ? item.lab_band[1] : 0
  );
  return top < 1 ? { factor: 1000, unit: 'µg/L' } : { factor: 1, unit: 'ppm' };
}

const PRINTED_TEMP = /^(?:°|deg\s*)?\s*([FC])$/i;

// What a row is drawn in: the keeper's units, never ours. Salinity is
// judged in ppt but a refractometer keeper reads S.G. (and fmtNum would
// round 1.0262 to 1.03); a temperature the lab printed in the other scale
// is turned into the tank's, with the lab's own print kept beside it.
// conv maps a number in the item's unit to the shown one, fmt prints it.
export function displayFor(item, { salinityUnit = 'ppt', tempUnit = 'F' } = {}) {
  if (item.key === 'salinity' && item.unit === 'ppt') {
    if (salinityUnit === 'sg') {
      const d = sgDecimals(item.band || item.lab_band, item.value);
      return {
        conv: (v) => pptToSg(v, d),
        fmt: (v) => (Number.isFinite(v) ? v.toFixed(d) : '—'),
        unit: 'S.G.',
        printed: null,
      };
    }
    return { conv: (v) => v, fmt: fmtNum, unit: salinityUnitLabel(salinityUnit), printed: null };
  }
  // Modern Reef prints specific gravity as its own row ("1.0256 sg"); at two
  // decimals it read 1.03, about 39 ppt.
  // A salinity printed in S.G. by a lab we cannot convert reads the same way.
  if (
    (item.source === 'lab' || item.unconvertible_unit) &&
    String(item.unit || '')
      .trim()
      .toLowerCase() === 'sg'
  ) {
    return {
      conv: (v) => v,
      fmt: (v) => (Number.isFinite(v) ? v.toFixed(4) : '—'),
      unit: 'S.G.',
      printed: null,
    };
  }
  const t = item.source === 'lab' ? PRINTED_TEMP.exec(String(item.unit || '').trim()) : null;
  if (t && t[1].toUpperCase() !== tempUnit) {
    const conv = t[1].toUpperCase() === 'F' ? fToC : cToF;
    return {
      conv,
      fmt: (v) => (Number.isFinite(v) ? v.toFixed(1) : '—'),
      unit: tempLabel(tempUnit),
      printed: `${item.value} ${item.unit}`,
    };
  }
  const { factor, unit } = displayScale(item);
  return { conv: (v) => v * factor, fmt: fmtNum, unit, printed: null };
}

function fmtDate(d) {
  if (!d) return 'Undated';
  return new Date(d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function bandGeometry(band, value) {
  const [lo, hi] = band;
  // Padding is a share of the top of the range, capped at three widths of
  // the range: an S.G. band (1.0249-1.0264) padded by a fifth of 1.026 was
  // drawn one percent wide, with a high 1.0280 inside it.
  const span = hi - lo;
  const w = Math.max(span, Math.min(Math.abs(hi) * 0.2, 3 * span), 1e-9);
  const min = Math.min(lo - w, value);
  const max = Math.max(hi + w, value);
  const clamp = (x) => Math.max(0, Math.min(1, (x - min) / (max - min || 1)));
  return { fillFrom: clamp(lo) * 100, fillTo: clamp(hi) * 100, pos: clamp(value) * 100 };
}

// The bar is drawn in the element's own unit, before the keeper's display
// conversion: S.G. sits on 1.0, so padding by a share of the range's top
// drew 1.025-1.027 one percent wide with a high salinity inside it. A
// registry contaminant is judged on our detection level, not the lab's
// target, so a bar against the lab's range put DETECTED inside the green
// and CLEAN outside it.
export function bandBarFor(item) {
  if (!item || item.unconvertible_unit || item.not_reported) return null;
  if (item.status === 'tracked') return null;
  if (item.kind === 'contaminant' && item.source !== 'lab') return null;
  const b = item.band || item.lab_band;
  if (!Array.isArray(b) || !(b[1] > b[0])) return null;
  const v = Number(item.value);
  return Number.isFinite(v) ? bandGeometry(b, v) : null;
}

function BandBar({ g, color }) {
  return (
    <div
      style={{
        position: 'relative',
        height: 8,
        borderRadius: 5,
        background: 'var(--surface)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: `${g.fillFrom}%`,
          width: `${Math.max(g.fillTo - g.fillFrom, 1)}%`,
          top: 0,
          bottom: 0,
          background: 'var(--ok, #4ade80)',
          opacity: 0.28,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: `calc(${g.pos}% - 3px)`,
          top: -2,
          width: 6,
          height: 12,
          borderRadius: 3,
          background: color,
        }}
      />
    </div>
  );
}

function Pill({ status }) {
  if (!STATUS_LABEL[status]) return null;
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '.04em',
        padding: '2px 7px',
        borderRadius: 6,
        color: 'var(--cyan-text, #041a33)',
        background: STATUS_COLOR[status],
      }}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

// One element across every test, dated, with the range shaded behind it.
function TrendChart({ series, band, unit, fmt = fmtNum }) {
  const pts = series.filter((p) => Number.isFinite(p.value));
  if (pts.length < 2) {
    return (
      <div style={{ fontSize: 14, color: 'var(--text-dim)', marginTop: 8 }}>
        A trend line appears here after your next ICP test.
      </div>
    );
  }
  const W = 320;
  const H = 130;
  const pad = { l: 44, r: 12, t: 10, b: 24 };
  const vals = pts.map((p) => p.value).concat(band || []);
  let min = Math.min(...vals);
  let max = Math.max(...vals);
  if (max - min < 1e-9) {
    min -= 1;
    max += 1;
  }
  const span = max - min;
  min -= span * 0.1;
  max += span * 0.1;
  const t0 = new Date(pts[0].sampled_at).getTime();
  const t1 = new Date(pts[pts.length - 1].sampled_at).getTime();
  const tx = (p, i) =>
    t1 > t0
      ? pad.l + ((new Date(p.sampled_at).getTime() - t0) / (t1 - t0)) * (W - pad.l - pad.r)
      : pad.l + (i / (pts.length - 1)) * (W - pad.l - pad.r);
  const ty = (v) => pad.t + (1 - (v - min) / (max - min)) * (H - pad.t - pad.b);
  const line = pts
    .map((p, i) => `${i ? 'L' : 'M'}${tx(p, i).toFixed(1)},${ty(p.value).toFixed(1)}`)
    .join(' ');
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      style={{ marginTop: 10, maxWidth: 520, display: 'block' }}
      role="img"
      aria-label={`Trend across ${pts.length} tests`}
    >
      {band ? (
        <rect
          x={pad.l}
          y={ty(band[1])}
          width={W - pad.l - pad.r}
          height={Math.max(1, ty(band[0]) - ty(band[1]))}
          fill="var(--ok, #4ade80)"
          opacity="0.14"
        />
      ) : null}
      <line x1={pad.l} x2={pad.l} y1={pad.t} y2={H - pad.b} stroke="var(--surface-border)" />
      <text x={pad.l - 6} y={ty(max) + 10} textAnchor="end" fontSize="10" fill="var(--text-dim)">
        {fmt(max)}
      </text>
      <text x={pad.l - 6} y={ty(min)} textAnchor="end" fontSize="10" fill="var(--text-dim)">
        {fmt(min)}
      </text>
      <path d={line} fill="none" stroke="var(--cyan)" strokeWidth="2" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle
            cx={tx(p, i)}
            cy={ty(p.value)}
            r={i === pts.length - 1 ? 4 : 3}
            fill="var(--cyan)"
          />
          <title>{`${fmtDate(p.sampled_at)}: ${fmt(p.value)} ${unit}`}</title>
        </g>
      ))}
      <text x={pad.l} y={H - 6} fontSize="10" fill="var(--text-dim)">
        {fmtDate(pts[0].sampled_at)}
      </text>
      <text x={W - pad.r} y={H - 6} textAnchor="end" fontSize="10" fill="var(--text-dim)">
        {fmtDate(pts[pts.length - 1].sampled_at)}
      </text>
    </svg>
  );
}

// Every point as words under the chart: a finger cannot hover the dots.
function TrendValues({ series, unit, fmt = fmtNum }) {
  const pts = series.filter((p) => Number.isFinite(p.value));
  if (pts.length < 2) return null;
  return (
    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        margin: '6px 0 0',
        fontSize: 14,
        color: 'var(--text-muted)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px 14px',
      }}
    >
      {pts.map((p, i) => (
        <li key={i} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmtDate(p.sampled_at)}: {fmt(p.value)} {unit}
        </li>
      ))}
    </ul>
  );
}

function ElementRow({ item, series, units }) {
  const [open, setOpen] = useState(false);
  const judged = !item.unconvertible_unit;
  const { conv, fmt, unit, printed } = displayFor(item, units);
  const scale = (b) => (b ? b.map((x) => conv(x)) : null);
  const band = scale(item.band);
  const labBand = scale(item.lab_band);
  const chartBand = band || labBand;
  const shown = conv(item.value);
  const bar = bandBarFor(item);
  const scaled = series.map((p) => ({ ...p, value: conv(p.value) }));
  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--surface-border)' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          minHeight: 44,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 16 }}>{item.label}</span>
          {item.symbol ? (
            <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>{item.symbol}</span>
          ) : null}
          <Pill status={item.status} />
        </span>
        <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 16, fontWeight: 600 }}>
          {item.not_reported ? (
            <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>not reported</span>
          ) : item.below_detection ? (
            <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>not detected</span>
          ) : (
            <>
              {fmt(shown)}{' '}
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 14 }}>
                {unit}
              </span>
            </>
          )}
        </span>
      </button>
      {bar ? (
        <div style={{ marginTop: 7 }}>
          <BandBar g={bar} color={STATUS_COLOR[item.status]} />
        </div>
      ) : null}
      <div
        style={{
          marginTop: 5,
          fontSize: 13.5,
          color: 'var(--text-dim)',
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        {band && judged ? (
          <span>
            {item.band_source === 'tank' ? 'Your range' : 'Reef range'} {fmt(band[0])}–
            {fmt(band[1])} {unit}
          </span>
        ) : null}
        {labBand && judged ? (
          <span>
            Lab target{' '}
            {labBand[0] === labBand[1] ? fmt(labBand[0]) : `${fmt(labBand[0])}–${fmt(labBand[1])}`}{' '}
            {unit}
          </span>
        ) : null}
        {printed && !item.below_detection ? <span>The lab printed {printed}</span> : null}
        {item.converted_from ? (
          <span>Worked out from the lab&rsquo;s {item.converted_from} figure</span>
        ) : null}
      </div>
      {!judged ? (
        <div style={{ marginTop: 6, fontSize: 14.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          Not judged: the lab reported {item.unconvertible_unit}, which can&apos;t be converted to
          this element&apos;s range.
        </div>
      ) : null}
      {item.not_reported ? (
        <div style={{ marginTop: 6, fontSize: 14.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          The lab printed no number for {item.label.toLowerCase()} on this report. Your own tests
          are what count.
        </div>
      ) : null}
      {item.logged_as === 'salinity' ? (
        <div style={{ marginTop: 6, fontSize: 14.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          The lab&rsquo;s own S.G. for the same water. It may not match the Salinity row to the last
          decimal; the Salinity row under Majors is the one TankSensei checks.
        </div>
      ) : null}
      {item.advice ? (
        <div style={{ marginTop: 6, fontSize: 14.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          {item.advice}
        </div>
      ) : null}
      {open && !item.not_reported ? (
        <>
          <TrendChart series={scaled} band={chartBand} unit={unit} fmt={fmt} />
          <TrendValues series={scaled} unit={unit} fmt={fmt} />
        </>
      ) : null}
    </div>
  );
}

function isQuietMetal(item) {
  return item.status === 'clean' && (item.below_detection || item.value === 0);
}

function Section({ title, children }) {
  return (
    <section style={{ marginTop: 22 }}>
      <h3
        className="font-heading"
        style={{
          fontSize: 13,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--text-dim)',
          margin: '0 0 4px',
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function ConsumptionLine({ consumption }) {
  const parts = [];
  for (const k of ['alkalinity', 'calcium', 'magnesium']) {
    const c = consumption?.elements?.[k];
    if (c && c.direction === 'falling' && Math.abs(c.delta_per_day) > 0) {
      parts.push(`${c.label} ${fmtNum(Math.abs(c.delta_per_day))} ${c.unit}/day`);
    }
  }
  if (!parts.length) return null;
  return (
    <div style={{ fontSize: 14.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
      Fell since the previous test (~{consumption.days} days):{' '}
      <b style={{ color: 'var(--text)' }}>{parts.join(' · ')}</b>. That is the drop left after your
      dosing and water changes, not the tank&apos;s whole use: if those stayed the same, it is how
      far they fell short.
    </div>
  );
}

const STATUS_WORD = {
  ok: 'OK',
  clean: 'clean',
  low: 'low',
  high: 'high',
  detected: 'detected',
};

function Changes({ changes, prevDate }) {
  if (!changes?.length) return null;
  return (
    <Section title={`Changed since ${fmtDate(prevDate)}`}>
      {changes.map((c) => (
        <div
          key={c.key}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '8px 0',
            borderTop: '1px solid var(--surface-border)',
            fontSize: 15.5,
          }}
        >
          <span style={{ fontWeight: 600 }}>{c.label}</span>
          <span style={{ color: c.improved ? 'var(--ok, #4ade80)' : 'var(--amber, #f0b429)' }}>
            {STATUS_WORD[c.from] || c.from} → {STATUS_WORD[c.to] || c.to}
          </span>
        </div>
      ))}
    </Section>
  );
}

export default function IcpView({ tank, onClose, onAddReport = null, backLabel = 'Home' }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [testId, setTestId] = useState(null);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [salinityUnit] = useState(readSalinityUnit);
  const units = { salinityUnit, tempUnit: getDisplayUnits(tank).temp };

  useEffect(() => {
    let alive = true;
    setErr('');
    const qs = testId ? `?test=${encodeURIComponent(testId)}` : '';
    api(`/api/tanks/${tank.id}/icp${qs}`)
      .then((r) => alive && setData(r))
      .catch((e) => alive && setErr(e.message || 'Could not load ICP results'));
    return () => {
      alive = false;
    };
  }, [tank.id, testId, reload]);

  const overview = data?.overview;
  const tests = data?.tests || [];
  const current = tests.find((t) => t.id === data?.current_id);
  const prev = tests.find((t) => t.id === data?.previous_id);
  const seriesFor = (key) =>
    tests
      .map((t) => ({ sampled_at: t.sampled_at, value: Number(t.elements?.[key]) }))
      .filter((p) => Number.isFinite(p.value));
  const canDelete = !tank.role || tank.role === 'owner';

  const removeTest = async () => {
    if (!current) return;
    const ok = window.confirm(
      `Delete the ${fmtDate(current.sampled_at)} ICP test and its stored report? Readings it added to your parameter history stay.`
    );
    if (!ok) return;
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/icp/${current.id}`, { method: 'DELETE' });
      setTestId(null);
      setReload((n) => n + 1);
    } catch (e) {
      setErr(e.message || 'Could not delete that test');
    } finally {
      setBusy(false);
    }
  };

  const tone = overview?.summary?.contaminants_detected
    ? 'var(--alert)'
    : overview?.summary?.attention_count
      ? 'var(--amber, #f0b429)'
      : 'var(--ok, #4ade80)';
  const pdfUrl = current?.pdf ? `/api/tanks/${tank.id}/icp/${current.id}/pdf` : null;

  return (
    <div className="lb-page" aria-labelledby="icp-title">
      <header className="lb-page-header">
        <button
          type="button"
          className="ts-back"
          aria-label={`Back to ${backLabel}`}
          onClick={onClose}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>{backLabel}</span>
        </button>
        <div className="ts-page-title">
          <div className="lb-eyebrow">{tank.name}</div>
          <h2 id="icp-title" className="font-heading lb-title">
            ICP results
          </h2>
        </div>
      </header>

      <div className="lb-page-body" style={{ paddingBottom: 120 }}>
        {/* Feedback #80: the page told a keeper to go to Log for the upload
            and offered no way there, and only while it was empty. A viewer
            cannot log, so gets no button. */}
        {onAddReport && tank.role !== 'viewer' ? (
          <button
            type="button"
            onClick={onAddReport}
            className="font-heading"
            style={{
              minHeight: 44,
              padding: '8px 14px',
              marginBottom: 12,
              borderRadius: 999,
              color: 'var(--cyan)',
              border: '1px solid var(--accent-border)',
              background: 'transparent',
              fontSize: 12,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
            }}
          >
            + Add a report
          </button>
        ) : null}
        {err ? (
          <p role="alert" style={{ color: 'var(--alert)' }}>
            {err}
          </p>
        ) : null}
        {!data && !err ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : null}
        {data && !tests.length && tank.role === 'viewer' ? (
          <p style={{ color: 'var(--text-muted)', lineHeight: 1.55 }}>
            No ICP test on this tank yet.
          </p>
        ) : null}
        {data && !tests.length && tank.role !== 'viewer' ? (
          <p style={{ color: 'var(--text-muted)', lineHeight: 1.55 }}>
            No ICP test yet. Add your lab&apos;s PDF, CSV or XLSX, older reports too: each keeps its
            own date. Triton, ATI, Oceamo, Modern Reef and Tropic Marin are read automatically, and
            Reef Labs from its XLSX download. A PDF report is kept here so you can open it again any
            time.
          </p>
        ) : null}

        {tests.length > 1 ? (
          <div
            role="tablist"
            aria-label="ICP tests"
            style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}
          >
            {[...tests].reverse().map((t) => {
              const active = t.id === data?.current_id;
              const n = t.summary?.attention_count || 0;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTestId(String(t.id))}
                  style={{
                    flex: 'none',
                    padding: '8px 12px',
                    borderRadius: 12,
                    border: `1px solid ${active ? 'var(--accent-border)' : 'var(--surface-border)'}`,
                    background: active ? 'var(--accent-bg)' : 'var(--surface)',
                    color: 'var(--text)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{fmtDate(t.sampled_at)}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>
                    {t.lab || 'ICP'} · {n ? `${n} to watch` : 'all clear'}
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}

        {overview ? (
          <>
            <div
              style={{
                marginTop: 14,
                padding: '14px 16px',
                borderRadius: 14,
                border: `1px solid ${tone}`,
                background: 'var(--surface)',
              }}
            >
              <div style={{ fontSize: 14, color: 'var(--text-dim)' }}>
                {current?.lab || 'ICP'} · {fmtDate(current?.sampled_at)}
              </div>
              <div style={{ fontSize: 19, fontWeight: 700, color: tone, marginTop: 2 }}>
                {overview.summary.headline}
              </div>
              {overview.summary.detail ? (
                <div style={{ fontSize: 15, color: 'var(--text-muted)', marginTop: 3 }}>
                  {overview.summary.detail}
                </div>
              ) : null}
              <ConsumptionLine consumption={data.consumption} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                {pdfUrl ? (
                  <>
                    <a href={pdfUrl} target="_blank" rel="noopener" style={chip}>
                      Open report (PDF)
                    </a>
                    <a href={`${pdfUrl}?download=1`} style={chip}>
                      Download
                    </a>
                  </>
                ) : (
                  <span style={{ fontSize: 13.5, color: 'var(--text-dim)' }}>
                    No PDF stored for this test.
                  </span>
                )}
                {canDelete ? (
                  <button
                    type="button"
                    onClick={removeTest}
                    disabled={busy}
                    style={{ ...chip, color: 'var(--alert)' }}
                  >
                    {busy ? 'Deleting…' : 'Delete test'}
                  </button>
                ) : null}
              </div>
            </div>

            <Changes changes={data.changes} prevDate={prev?.sampled_at} />

            {overview.attention?.length ? (
              <Section title="Needs attention">
                {overview.attention.map((item) => (
                  <ElementRow
                    key={`a-${item.key}`}
                    item={item}
                    series={seriesFor(item.key)}
                    units={units}
                  />
                ))}
              </Section>
            ) : null}

            {GROUP_ORDER.map((g) => {
              const items = overview.groups?.[g] || [];
              if (!items.length) return null;
              // Twenty "not detected" metals as twenty rows bury the one that
              // was detected; they read better as one line.
              const quiet = g === 'contaminant' ? items.filter(isQuietMetal) : [];
              const shown = items.filter((i) => !quiet.includes(i));
              return (
                <Section key={g} title={GROUP_LABEL[g]}>
                  {shown.map((item) => (
                    <ElementRow
                      key={item.key}
                      item={item}
                      series={seriesFor(item.key)}
                      units={units}
                    />
                  ))}
                  {quiet.length ? (
                    <div
                      style={{
                        padding: '10px 0',
                        borderTop: '1px solid var(--surface-border)',
                        fontSize: 15,
                        lineHeight: 1.55,
                      }}
                    >
                      <Pill status="clean" />{' '}
                      <span style={{ color: 'var(--text-muted)' }}>
                        Not detected: {quiet.map((i) => i.label).join(', ')}
                      </span>
                    </div>
                  ) : null}
                </Section>
              );
            })}

            <p style={{ fontSize: 13.5, color: 'var(--text-dim)', marginTop: 22, lineHeight: 1.5 }}>
              Tap any element for its trend. &ldquo;Your range&rdquo; is the range you keep for that
              test in this tank; reef ranges are community guidance, not lab thresholds; where
              TankSensei has neither, the lab&apos;s own target is used and labelled. We show the
              lab&apos;s numbers with reef context: we never re-measure or tell you to dose.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}

const chip = {
  fontSize: 14,
  padding: '7px 12px',
  minHeight: 44,
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 999,
  border: '1px solid var(--surface-border)',
  background: 'transparent',
  color: 'var(--cyan)',
  textDecoration: 'none',
  cursor: 'pointer',
};
